/* ============================================================================
   ergonix-modal.js — pairs with assets/ergonix-modal.css. SHIP THE TWO TOGETHER.
   ----------------------------------------------------------------------------
   Must stay byte-identical between "ERGONIX.LT WEB" and "ERGONIX.PL WEB".

   Two things live here, in this order:
     1. click-outside-to-close for the Omnisend back-in-stock popup (below)
     2. the generic dialog engine for the .ergonix-modal primitives in
        ergonix-modal.css section C (bottom of this file)

   Every overlay this theme owns closes when you click the dimmed area:
     snippets/faq-contact-modal.liquid   onclick="closeFAQModal()" on the overlay
     sections/ergonix-omnidesk-hero      if (e.target === overlay) UpsellPopup.close()
     sections/ergonix-quiz-popup         if (e.target === this) QuizPopup.close()
     assets/cart.js                      cart-overlay click, desktop only
   Omnisend's popup was the only one that did not, so a customer who expected the
   rest of the site's behaviour had to find the X. Escape already worked.

   This does not reimplement closing: it clicks Omnisend's own close button, so
   Omnisend still runs its own teardown and analytics.

   SCOPE, deliberately narrow: only forms the theme itself opens, i.e. those
   whose id appears in a data-omnisend-form attribute on the page (the
   back-in-stock button in snippets/ergonix-out-of-stock-card.liquid). Omnisend's
   own newsletter and discount popups are left exactly as they are, because
   making those easier to dismiss is a conversion decision, not a styling one.
   ========================================================================= */

(function () {
  'use strict';

  var WRAPPER = 'omnisend-forms-wrapper';

  // Form ids the theme opens itself. Read at click time, not at load: the
  // sold-out card is server-rendered but the variant picker can swap it in.
  function themeOwnedFormIds() {
    var ids = [];
    document.querySelectorAll('[data-omnisend-form]').forEach(function (el) {
      var id = el.getAttribute('data-omnisend-form');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  /*
     "Outside" is anything in the popup that is not the white panel.
     Do NOT test for the scrim element alone: Omnisend's -content flex child is
     90% of the viewport wide, so on desktop most of the dimmed area is -content
     sitting on top of the scrim, and a scrim-only test made the click work near
     the screen edges and do nothing everywhere else.
     -close-button lives inside -content-inner, so it is excluded here and its
     own handler does the closing, no double fire.
  */
  document.addEventListener('click', function (e) {
    var el = e.target;
    if (!el || !el.closest) return;
    if (!el.closest('#' + WRAPPER)) return;
    if (el.closest('[class*="omnisend-form-"][class$="-content-inner"]')) return;

    // #omnisend-forms-wrapper / -container do not match: the trailing hyphen
    // in the prefix rules out "omnisend-forms-".
    var root = el.closest('[id^="omnisend-form-"]');
    if (!root) return;

    var owned = themeOwnedFormIds();
    if (!owned.some(function (id) { return root.id === 'omnisend-form-' + id; })) return;

    var closeBtn = root.querySelector('[id$="-close-action"]');
    if (closeBtn) closeBtn.click();
  });
})();


/* ============================================================================
   Generic dialog engine for the .ergonix-modal primitives in ergonix-modal.css §C.
   ----------------------------------------------------------------------------
   SHIPS WITH assets/ergonix-modal.css AND, for its first consumer, with
   sections/ergonix-omnidesk-hero.liquid (LT) plus its hand-mirrored PL copy.
   These two assets auto-sync LT->PL; the section is CONTENT_ADAPTED and is
   mirrored by hand, so push the ASSETS FIRST and the section second.

   Fail-safe in both directions, which is what makes a half-shipped bundle
   harmless (SENSITIVE-CONTRACTS #1):
     - engine present, no [data-erx-modal] on the page  -> no-op
     - triggers present, engine missing                 -> the triggers are
       rendered `disabled` by the server and ONLY enableTriggers() below ever
       removes that, so the row degrades to the inert text it was before.

   First consumer: the three feature bullets in the desk PDP buy box. They were
   plain text that read like controls and took ~539 dead taps a month (Clarity).

   Markup contract, on top of the §C one:
     trigger  <button data-erx-modal="<dialog id>" aria-haspopup="dialog" disabled>
     dialog   <div class="ergonix-modal" id="<dialog id>" hidden> … </div>
     closers  any [data-modal-close] inside the dialog (scrim and the X)
     media    <video data-src="…"> is fetched on first open and torn down on
              close, so a page with three dialogs downloads nothing until asked.
   ========================================================================= */

(function () {
  'use strict';

  if (window.ErgonixModal) return; // idempotent if this file is ever double-loaded

  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),' +
                  'select:not([disabled]),textarea:not([disabled]),' +
                  'video[controls],[tabindex]:not([tabindex="-1"])';

  var openEl = null;
  var lastFocus = null;

  function reduceMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function startMedia(root) {
    var v = root.querySelector('video[data-src]');
    if (!v) return;
    if (!v.getAttribute('src')) {
      v.setAttribute('src', v.getAttribute('data-src'));
      v.load();
    }
    // Reduced motion: never autoplay. Show a still frame and an explicit control.
    if (reduceMotion()) { v.controls = true; return; }
    v.controls = false;
    var p = v.play();
    if (p && p.catch) p.catch(function () { v.controls = true; }); // iOS Low Power Mode
  }

  function stopMedia(root) {
    var v = root.querySelector('video[src]');
    if (!v) return;
    v.pause();
    v.removeAttribute('src');
    v.load(); // frees the decoder, same teardown as ergonix-stories.js
  }

  window.ErgonixModal = {
    open: function (id) {
      var root = document.getElementById(id);
      if (!root || root === openEl) return;
      if (openEl) this.close();
      lastFocus = document.activeElement;
      root.hidden = false;
      /* A CLASS, not document.body.style.overflow. UpsellPopup in
         ergonix-omnidesk-hero.liquid writes that inline property directly and
         inline beats a stylesheet, so keeping the two mechanisms separate is
         what stops one overlay clearing the other's scroll lock. */
      document.body.classList.add('ergonix-modal-lock');
      openEl = root;
      startMedia(root);
      var close = root.querySelector('.ergonix-modal__close');
      if (close && close.focus) close.focus();
    },
    close: function () {
      if (!openEl) return;
      stopMedia(openEl);
      openEl.hidden = true;
      openEl = null;
      document.body.classList.remove('ergonix-modal-lock');
      if (lastFocus && lastFocus.focus) lastFocus.focus();
      lastFocus = null;
    }
  };

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    var opener = t.closest('[data-erx-modal]');
    if (opener && !opener.disabled) {
      e.preventDefault();
      window.ErgonixModal.open(opener.getAttribute('data-erx-modal'));
      return;
    }
    if (openEl && t.closest('[data-modal-close]')) {
      e.preventDefault();
      window.ErgonixModal.close();
    }
  });

  /* CAPTURE phase on purpose: ergonix-omnidesk-hero.liquid registers a
     BUBBLE-phase document keydown that calls UpsellPopup.close(). Taking
     Escape here first and stopping propagation guarantees one Escape closes
     exactly one overlay. */
  document.addEventListener('keydown', function (e) {
    if (!openEl) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      window.ErgonixModal.close();
      return;
    }
    if (e.key !== 'Tab') return;

    var panel = openEl.querySelector('.ergonix-modal__panel');
    if (!panel) return;
    var items = Array.prototype.filter.call(
      panel.querySelectorAll(FOCUSABLE),
      function (el) { return el.offsetParent !== null || el === document.activeElement; }
    );
    if (!items.length) { e.preventDefault(); return; }

    var first = items[0];
    var last = items[items.length - 1];
    if (!panel.contains(document.activeElement)) {
      e.preventDefault(); first.focus();
    } else if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  }, true);

  /* The ONLY thing that makes a trigger interactive. See the header. */
  function enableTriggers() {
    var nodes = document.querySelectorAll('[data-erx-modal][disabled]');
    Array.prototype.forEach.call(nodes, function (b) { b.disabled = false; });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enableTriggers);
  } else {
    enableTriggers();
  }
  // Restored from bfcache: re-enable, and never leave a stale open dialog behind.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    if (openEl) window.ErgonixModal.close();
    enableTriggers();
  });
})();
